import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { consumeReturnUrl, getSessionUser } from "shared/auth/auth";
import { useAuth } from "shared/contexts/AuthContext";
import { sendGTMEvent } from "shared/services/GTM";
import { getPlatformType } from "shared/utils/platform";

const SESSION_RETRY_DELAYS = [0, 300, 700, 1200];

const wait = (milliseconds: number) =>
  new Promise((resolve) => window.setTimeout(resolve, milliseconds));

async function getSessionUserWithRetry() {
  let lastError: unknown;

  for (const delay of SESSION_RETRY_DELAYS) {
    if (delay > 0) await wait(delay);

    try {
      const user = await getSessionUser();
      if (user) return user;
    } catch (error) {
      lastError = error;
    }
  }

  throw lastError ?? new Error("Sessão não encontrada.");
}

export default function AuthCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { completeAuthentication } = useAuth();
  const handled = useRef(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (handled.current) return;
    handled.current = true;

    const finishAuthentication = async () => {
      if (searchParams.get("auth") !== "true") {
        sendGTMEvent("authLoginError", {
          platform: getPlatformType(),
          errorStage: "callback_validation",
          errorType: "invalid_callback",
        });
        setError(
          "Não foi possível concluir a autenticação. Tente entrar novamente.",
        );
        return;
      }

      const cleanUrl = new URL(window.location.href);
      cleanUrl.searchParams.delete("auth");
      window.history.replaceState(
        {},
        "",
        `${cleanUrl.pathname}${cleanUrl.search}${cleanUrl.hash}`,
      );

      try {
        const user = await getSessionUserWithRetry();
        completeAuthentication(user);
        const returnPath = consumeReturnUrl();

        sendGTMEvent("authLoginSuccess", {
          platform: getPlatformType(),
          returnPath: new URL(returnPath, window.location.origin).pathname,
        });

        navigate(returnPath, { replace: true });
      } catch {
        sendGTMEvent("authLoginError", {
          platform: getPlatformType(),
          errorStage: "session_recovery",
          errorType: "session_not_found",
        });
        setError(
          "A autenticação foi concluída, mas não foi possível consultar sua sessão. Tente novamente.",
        );
      }
    };

    void finishAuthentication();
  }, [completeAuthentication, navigate, searchParams]);

  return (
    <main role="status" style={{ padding: "2rem", textAlign: "center" }}>
      {error ?? "Concluindo sua autenticação..."}
    </main>
  );
}
